import {BannerDataIframe} from '@core-client/ads/banner/data/BannerDataIframe';
import {BannerDataIframeNull} from '@core-client/ads/banner/data/BannerDataIframeNull';
import {BannerDataImage} from '@core-client/ads/banner/data/BannerDataImage';
import {BannerDataNull} from '@core-client/ads/banner/data/BannerDataNull';
import {BannerDataText} from '@core-client/ads/banner/data/BannerDataText';
import {IBannerData} from '@core-client/ads/banner/data/IBannerData';
import {DEFAULT_VIEWPORT_THRESHOLD} from '../config/BannerCfg';
import {getBannerStatLogger, getErrorOperationWithSuffix} from '../util/BannerStat';
import {HtmlRenderer} from '../util/HtmlRenderer';
import type {ValueProvider} from "../util/ValueProvider";
import {detect as detectWhiteList} from '../util/wld';
import type {SimpleBanner} from '../variant/SimpleBanner';

import {BannerHeightChangeWatcher} from './BannerHeightChangeWatcher';
import {IframeBannerRenderer} from './render/IframeBannerRenderer';
import {IframeNullBannerRenderer} from './render/IframeNullBannerRenderer';
import {ImageBannerRenderer} from './render/ImageBannerRenderer';
import {NullBannerRenderer} from './render/NullBannerRenderer';
import {TextBannerRenderer} from './render/TextBannerRenderer';

declare global {
    interface Window {
        trg_gc: () => void;
        rb_js_bannerClick: (id: string) => void;
    }
}

const CLICK_PARAM_PREFIX = "nbnr_";
const registry: Record<string, BannerView | null> = {};

let next_id = 0;
let renderer: HtmlRenderer | null = null;

function invokeTrgGc() {
    if (typeof window.trg_gc === 'function') {
        window.trg_gc();
    }
}

function getRenderer(): HtmlRenderer {
    if (renderer === null) {
        renderer = new HtmlRenderer();
    }
    return renderer;
}

function handleBannerClick(id: string) {
    const uid = id.slice(CLICK_PARAM_PREFIX.length);
    const view = registry[uid];
    if (view && view.banner !== null) {
        view.banner.handleBannerClick();
    }
}

const THRESHOLD = 1000 * 40;
let userActive = Date.now();

function userAction() {
    userActive = Date.now();
}

let userActionListenerAdded = false;

function listenUserAction() {
    if (!userActionListenerAdded) {
        window.addEventListener('scroll', userAction);
        document.body.addEventListener('mousemove', userAction);
        userActionListenerAdded = true;
    }
}

function isUserActive() {
    return (Date.now() - userActive) < THRESHOLD;
}

let whiteListDetected = true;

detectWhiteList().catch(() => {
    whiteListDetected = false;
});

export interface IBannerViewOptions {
    container: HTMLElement | null;
    /**
     * Указывает, что отображение и скрытие баннера осуществляется извне.
     */
    externalShow?: boolean;
    slot: ValueProvider<string> | null;
    adfTransport: boolean;
    inline?: boolean;
    inViewportThreshold?: number;
    onRender?: (banner: IBannerData) => void;
    onError?: (banner: IBannerData) => void;
}

const defaultOptions: IBannerViewOptions = {
    container: null,
    externalShow: false,
    slot: null,
    adfTransport: false,
    inline: false,
    onRender: undefined,
    onError: undefined
};

class BannerView {
    options: IBannerViewOptions;
    container: HTMLElement;
    uid: string;
    slot: string;
    place: string | null;
    adfTransport = false;
    inViewportThreshold: number;
    nullBannerData: IBannerData;

    heightChangeWatcher: BannerHeightChangeWatcher;

    iframeBannerRenderer: IframeBannerRenderer;
    iframeNullBannerRenderer: IframeNullBannerRenderer;
    imageBannerRenderer: ImageBannerRenderer;
    nullBannerRenderer: NullBannerRenderer;
    textBannerRenderer: TextBannerRenderer;

    banner: SimpleBanner | null = null;
    contentChangeObserver: MutationObserver | null = null;

    constructor(opts: IBannerViewOptions) {
        const options = {...defaultOptions, ...opts};
        this.options = options;
        this.container = options.container!;
        this.uid = '' + (next_id++);
        this.place = this.container ? this.container.getAttribute('data-place') : null;
        this.adfTransport = options.adfTransport;
        this.inViewportThreshold = typeof options.inViewportThreshold === 'number'
            ? options.inViewportThreshold
            : DEFAULT_VIEWPORT_THRESHOLD;
        this.nullBannerData = this.adfTransport ? BannerDataIframeNull.IFRAME_NULL : BannerDataNull.NULL;

        if (options.slot) {
            this.slot = options.slot.getValue();
            if (this.slot) {
                getRenderer().setBannerPlace(options.container!, this.slot);
            }
        } else {
            this.slot = '';
        }

        this.heightChangeWatcher = new BannerHeightChangeWatcher({bannerView: this});
        this.iframeBannerRenderer = new IframeBannerRenderer(this);
        this.iframeNullBannerRenderer = new IframeNullBannerRenderer(this);
        this.imageBannerRenderer = new ImageBannerRenderer(this);
        this.nullBannerRenderer = new NullBannerRenderer(this);
        this.textBannerRenderer = new TextBannerRenderer(this);

        listenUserAction();
    }

    /**
     * true for banners in main document (not in poplayers)
     */
    isInline(): boolean {
        return this.options.inline!;
    }

    attach(banner: SimpleBanner) {
        registry[this.uid] = this;
        this.banner = banner;

        this.heightChangeWatcher.start();
        this.createBannerDomObserver(this.container);
    }

    detach() {
        this.destroyBannerDomObserver();
        this.heightChangeWatcher.stop();

        this.renderNullBanner();
        this.banner = null;
        registry[this.uid] = null;
    }

    public isInViewport(): boolean {

        try {
            if (!isUserActive()) {
                return false;
            }

            if (whiteListDetected) {
                return false;
            }

            if (document.visibilityState === 'hidden') {
                return false;
            }

            const el = this.container;
            const threshold = this.inViewportThreshold;
            const screenTop = window.pageYOffset;
            const screenHeight = window.innerHeight;
            const rect = el.getBoundingClientRect();
            const elPart = rect.height * threshold;
            const elTop = rect.top + screenTop;
            const elBottom = rect.bottom + screenTop;

            if (screenHeight < elPart) {
                return false;
            }

            const lowerBound = screenTop;
            const upperBound = screenHeight + screenTop;

            return ((elTop + elPart) < upperBound && elTop > lowerBound) ||
                ((elBottom - elPart) > lowerBound && elBottom < upperBound) ||
                (elTop < lowerBound && elBottom > upperBound);

        } catch (e) {
            getBannerStatLogger().errorEx(
                e as Error,
                "BannerViewJs",
                "isInViewport",
                getErrorOperationWithSuffix(this.slot)
            );
            return false;
        }
    }

    getContainer(): HTMLElement {
        return this.container;
    }

    getSlot(): string {
        return this.slot;
    }

    getPlace(): string {
        return this.place!;
    }

    isAdfTransport(): boolean {
        return this.adfTransport;
    }

    showContainer() {
        if (!this.options.externalShow) {
            this.container.style.display = 'block';
            // Не показываем пользователю контент в момент обновления
            this.container.style.visibility = 'hidden';
            // Через 100 мс показываем контейнер банера. Полагаем, что за это время все перерасчёты (привет, липучка!) будут выполнены
            // todo: wtf magic 100ms ?
            setTimeout(() => {
                if (this.container !== null) {
                    this.container.style.visibility = 'visible';
                }
            }, 100);
        }
    }

    hideContainer() {
        if (!this.options.externalShow) {
            this.container.style.display = 'none';
        }
    }

    setBannerHtml(html: string) {
        getRenderer().setBannerHtml(this.container, html);
    }

    renderNullBanner() {
        const banner = this.nullBannerData;
        if (banner instanceof BannerDataNull) {
            this.showBannerDivNull(banner);
        } else if (banner instanceof BannerDataIframeNull) {
            this.showBannerDivIframeNull(banner);
        }

        this.container.classList.toggle("banner_new__loaded", true);

        invokeTrgGc();

        if (this.options.onRender) {
            this.options.onRender(banner);
        }
    }

    renderBanner(banner: IBannerData) {
        let nullBanner = false;
        if (banner instanceof BannerDataNull) {
            this.showBannerDivNull(banner);
            nullBanner = true;
        } else if (banner instanceof BannerDataImage) {
            this.showBannerDivImage(banner);
        } else if (banner instanceof BannerDataText) {
            this.showBannerDivText(banner);
        } else if (banner instanceof BannerDataIframeNull) {
            this.showBannerDivIframeNull(banner);
            nullBanner = true;
        } else if (banner instanceof BannerDataIframe) {
            this.showBannerDivIframe(banner);
        }

        this.container.classList.toggle("banner_new__loaded", true);

        invokeTrgGc();

        if (this.options.onRender) {
            this.options.onRender(banner);
        }

        if (this.options.onError && nullBanner) {
            this.options.onError(banner);
        }
    }

    showBannerDivNull(empty: BannerDataNull) {
        this.nullBannerRenderer.showBannerDiv(empty);
    }

    showBannerDivText(text: BannerDataText) {
        this.textBannerRenderer.showBannerDiv(text);
    }

    showBannerDivImage(image: BannerDataImage) {
        this.imageBannerRenderer.showBannerDiv(image);
    }

    showBannerDivIframeNull(empty: BannerDataIframeNull) {
        this.iframeNullBannerRenderer.showBannerDiv(empty);
    }

    showBannerDivIframe(iframe: BannerDataIframe) {
        this.iframeBannerRenderer.showBannerDiv(iframe);
    }

    encodeClickParam(): string {
        return CLICK_PARAM_PREFIX + this.uid;
    }

    /**
     * Создаем MutationObserver, который следит за изменениеми DOM-а внутри баннера.
     * Довольно грубый, но рабочий способ определить то, что баннер был обновлен своим внутренним скриптом
     */
    createBannerDomObserver(elem: HTMLElement) {
        if (window.MutationObserver) {
            const watcher = this.heightChangeWatcher;
            const observer = new MutationObserver((mutations) => {
                for (let i = 0; i < mutations.length; ++i) {
                    const mutation = mutations[i];
                    if (mutation.addedNodes.length > 0) {
                        watcher.start();
                    }
                }
            });

            observer.observe(elem, {
                childList: true
            });
            this.contentChangeObserver = observer;
        }
    }

    destroyBannerDomObserver() {
        const observer = this.contentChangeObserver;
        if (observer) {
            observer.disconnect();
        }
    }

    removeOldIframe(newIframeId: string) {
        const iframeList = this.container.getElementsByTagName("iframe");
        for (let i = 0; i < iframeList.length; i++) {
            const iframe = iframeList.item(i)!;
            const frameId: string = iframe.id;
            if (newIframeId !== frameId) {
                const parentNode = iframe.parentNode;
                parentNode?.removeChild(iframe);
            }
        }
    }
}

window.rb_js_bannerClick = handleBannerClick;

export {BannerView};
